import React, { Component } from 'react';
import { CustomButton, Modifiers } from '@fw-nextgen/fw-components';
import Modal from 'rc-dialog';
import { FormattedMessage } from 'react-intl';

import ModalStore, {
  DialogActionType,
  IModalProps
} from '@app/src/common/store/ModalStore';

import IconModalSave from './assets/icon-modal-confirm.svg';
import IconModalDelete from './assets/icon-modal-delete.svg';
import IconModalWarning from './assets/icon-modal-warning.svg';
import IconModalCancel from './assets/icon-modal-cancel.svg';

import './ConfirmModal.scss';

export enum ConfirmModalType {
  CONFIRM,
  DELETE,
  SAVE,
  UPDATE,
  WARNING,
  CANCEL
}

const ConfirmationModalTypeValue = {
  [ConfirmModalType.CONFIRM]: <IconModalSave />,
  [ConfirmModalType.SAVE]: <IconModalSave />,
  [ConfirmModalType.DELETE]: <IconModalDelete />,
  [ConfirmModalType.UPDATE]: <IconModalSave />,
  [ConfirmModalType.WARNING]: <IconModalWarning />,
  [ConfirmModalType.CANCEL]: <IconModalCancel />
};

interface IProps extends IModalProps {
  title?: string;
  titleValues?: Record<string, React.ReactNode>;
  description?: React.ReactNode;
  cancelText?: string;
  submitText?: string;
  type?: ConfirmModalType;
  icon?: React.ReactNode;
  hideCancelButton?: boolean;
}

interface IExtraModalProps {
  hideCancelButton?: boolean;
  cancelText?: string;
  submitText?: string;
}

class ConfirmModal extends Component<IProps> {
  onSubmit = () => {
    this.props.onClose(DialogActionType.submit);
  };

  onCancel = () => {
    this.props.onClose(DialogActionType.cancel);
  };

  render() {
    const {
      title = this.props.title || this.titleText,
      titleValues,
      description = this.descriptionText ? (
        <FormattedMessage id={this.descriptionText} />
      ) : (
        ''
      ),
      cancelText = 'button.cancel',
      submitText = 'button.ok',
      type = ConfirmModalType.CONFIRM,
      icon = ConfirmationModalTypeValue[type],
      ...props
    } = this.props;

    return (
      <Modal
        {...props}
        onClose={() =>
          props.onClose(DialogActionType.cancel, { context: 'top' })
        }
        className="confirmation-modal"
        destroyOnClose={true}
        visible
      >
        <div className="confirmation-modal__wrapper">{icon}</div>
        {title && (
          <h3 className="confirmation-modal__title">
            <FormattedMessage
              id={title}
              values={titleValues}
              tagName={React.Fragment}
            />
          </h3>
        )}
        <p className="confirmation-modal__description">{description}</p>
        <div className="confirmation-modal__button-wrapper">
          {!this.props.hideCancelButton && (
            <CustomButton
              id="conf_cancel"
              value={cancelText}
              modifiers={[Modifiers.SMALL, Modifiers.OUTLINED]}
              onClick={this.onCancel}
            />
          )}
          <CustomButton
            id="conf_submit"
            value={submitText}
            modifiers={[Modifiers.SMALL]}
            onClick={this.onSubmit}
          />
        </div>
      </Modal>
    );
  }

  get titleText() {
    const { type } = this.props;
    let title = 'modal.confirm.title';
    switch (type) {
      case ConfirmModalType.DELETE:
        title = 'modal.confirm.delete.title';
        break;
      case ConfirmModalType.SAVE:
      case ConfirmModalType.UPDATE:
        title = 'modal.confirm.save.title';
        break;
      case ConfirmModalType.WARNING:
        title = '';
        break;
      default:
        break;
    }
    return title;
  }

  get descriptionText() {
    const { type } = this.props;
    let title = '';
    switch (type) {
      case ConfirmModalType.SAVE:
      case ConfirmModalType.UPDATE:
        title = 'modal.confirm.save.description';
        break;
      default:
        break;
    }
    return title;
  }
}

export default ConfirmModal;

export function getConfirmModal(
  titleId: string,
  descriptionId?: string,
  modalProps?: Partial<IProps>
) {
  return ModalStore.showModal(props => {
    return (
      <ConfirmModal
        {...props}
        description={
          descriptionId ? <FormattedMessage id={descriptionId} /> : ''
        }
        title={titleId}
        {...modalProps}
      />
    );
  });
}

export function getWarningModal(
  titleId: string,
  extraModalProps: IExtraModalProps = {}
) {
  return ModalStore.showModal(props => {
    return (
      <ConfirmModal
        {...props}
        type={ConfirmModalType.WARNING}
        title={titleId}
        {...extraModalProps}
      />
    );
  });
}

export function getDeleteModal(description?: string, translatable = true) {
  const descriptionValue = description ? (
    translatable ? (
      <FormattedMessage id={description} />
    ) : (
      description
    )
  ) : undefined;
  return ModalStore.showModal(props => (
    <ConfirmModal
      {...props}
      type={ConfirmModalType.DELETE}
      description={descriptionValue}
    />
  ));
}
